<template>
  <div>
    <div v-if="!isHotspots">
      <v-toolbar flat :height="44" class="layout-toolbar">
        <v-tabs
          v-model="tab"
          background-color="transparent"
          height="40"
          class="layout-tabs"
          @change="handleHeadlinesTabsChange"
        >
          <v-tab
            :key="headlineTabs.scribe"
            :href="'#' + headlineTabs.scribe"
            v-show="!hasGrds"
            :disabled="hasEssentials"
            class="px-0 tab-text"
          >
            {{ labels.scribe }}
          </v-tab>
          <v-tab
            :key="headlineTabs.breakingEvents"
            :href="'#' + headlineTabs.breakingEvents"
            v-if="showBreakingEvents"
            class="px-0 tab-text"
          >
            Breaking Events
          </v-tab>
          <v-tab
            :key="headlineTabs.events"
            :href="'#' + headlineTabs.events"
            v-show="!hasGrds"
            :disabled="hasEssentials"
            class="px-0 tab-text"
          >
            {{ labels.events }}
          </v-tab>
          <v-tab
            :key="headlineTabs.incidents"
            :href="'#' + headlineTabs.incidents"
            v-show="!hasGrds"
            v-if="locationTypeCountry || locationTypeCounty"
            class="px-0 tab-text"
            >{{ labels.verified_events }}</v-tab
          >
          <v-tab
            :key="headlineTabs.updates"
            :href="'#' + headlineTabs.updates"
            v-if="locationTypeCountry"
            class="px-0 tab-text"
            >{{ labels.cr_updates }}</v-tab
          >
          <v-tab
            :key="headlineTabs.travel"
            :href="'#' + headlineTabs.travel"
            v-if="locationTypeCountry && hasInternationalSOSAccess"
            class="px-0 tab-text"
            @click="trackTravel"
          >
            Travel
          </v-tab>
        </v-tabs>
      </v-toolbar>

      <v-tabs-items dark v-model="tab">
        <v-tab-item :key="headlineTabs.scribe" :value="headlineTabs.scribe">
          <ScribeLayout
            v-if="!hasGrds"
            :height="height"
            :location="location"
            :score="score"
            :date-selected="dateSelected"
          ></ScribeLayout>
        </v-tab-item>
        <v-tab-item
          :key="headlineTabs.breakingEvents"
          :value="headlineTabs.breakingEvents"
          v-if="showBreakingEvents"
        >
          <v-card dark color="#191a1a" flat rounded="0" class="pt-4">
            <slot name="events"></slot>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="headlineTabs.events" :value="headlineTabs.events">
          <v-card dark color="#191a1a" flat rounded="0" class="pt-4">
            <slot name="events"></slot>
          </v-card>
        </v-tab-item>
        <v-tab-item
          :key="headlineTabs.incidents"
          :value="headlineTabs.incidents"
          v-if="locationTypeCountry || locationTypeCounty"
        >
          <v-card dark color="#191a1a" flat rounded="0" class="pt-4">
            <slot name="incidents"></slot>
          </v-card>
        </v-tab-item>
        <v-tab-item
          :key="headlineTabs.updates"
          :value="headlineTabs.updates"
          v-if="locationTypeCountry"
        >
          <v-card
            dark
            color="#191a1a"
            flat
            rounded="0"
            class="pt-0 risk-updates-container"
          >
            <AnalysisList
              :additional-params="analysisParams"
              theme="dark"
              no-data-text="No headlines found"
              :show-inline-body="true"
              :show-sort-selection-btn="true"
            ></AnalysisList>
          </v-card>
        </v-tab-item>
        <v-tab-item
          :key="headlineTabs.travel"
          :value="headlineTabs.travel"
          v-if="locationTypeCountry && hasInternationalSOSAccess"
        >
          <v-card
            flat
            dark
            color="#191a1a"
            rounded="0"
            class="pt-0 risk-updates-container"
          >
            <AnalysisList
              :additional-params="travelParams"
              theme="dark"
              no-data-text="No Travel Alerts found"
              :show-inline-body="true"
              :show-sort-selection-btn="true"
            ></AnalysisList>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
    <div v-if="isHotspots">
      <slot name="events"></slot>
    </div>
  </div>
</template>

<script>
import { LABELS } from "@/common/constants/labels";
import {
  hasEssentialsAccess,
  hasGrdAccess,
  hasInternationalSOSAccess,
  hasBreakingEventsAccess,
} from "@/services/user/user.service";
import { getRiskCategories } from "@/services/controlRisks/risks.service";
import {
  RISK_RATING_SUB,
  TRAVEL_RISK_RATING_ID,
} from "@/common/constants/controlRisks/risks";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  HEADLINES_TAB_CHANGE: "headlines-tab-change",
};

export default {
  name: "MonitorHeadlines",
  components: {
    AnalysisList: () => import("@/components/analysis/AnalysisList"),
    ScribeLayout: () => import("@/components/scribe/ScribeLayout"),
  },
  props: {
    location: {
      type: Object,
      required: true,
    },
    score: {
      type: [String, Number],
    },
    height: {
      type: Number,
    },
    isHotspots: {
      type: Boolean,
    },
    dateSelected: {
      type: String,
    },
  },
  data() {
    const hasEssentials = hasEssentialsAccess();
    const hasGrds = hasGrdAccess();
    const headlineTabs = {
      scribe: "scribe",
      breakingEvents: "breakingEvents",
      events: "events",
      incidents: "incidents",
      updates: "updates",
      travel: "travel",
    };
    const selectedTab = hasEssentials
      ? headlineTabs.incidents
      : hasGrds
      ? headlineTabs.updates
      : headlineTabs.scribe;

    const isosAccess = hasInternationalSOSAccess();

    return {
      headlineTabs: headlineTabs,
      tab: selectedTab,
      loading: false,
      labels: LABELS,
      hasEssentials: hasEssentials,
      hasInternationalSOSAccess: isosAccess,
      hasGrds: hasGrds,
      hasBreakingEvents: hasBreakingEventsAccess(),
      travelParams: {
        riskCategories: TRAVEL_RISK_RATING_ID,
        aoiId: this.location.code,
      },
      analysisParams: {
        riskCategories: [],
        aoiId: this.location.code,
      },
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    locationTypeCountry() {
      return this.location && this.location.type === "country";
    },
    locationTypeCounty() {
      return this.location && this.location.type === "county";
    },
    locationName() {
      return this.location && this.location.type === "region"
        ? this.location.name
        : null;
    },
    showBreakingEvents() {
      return this.locationTypeCountry && this.hasBreakingEvents;
    },
  },
  methods: {
    async initialize() {
      this.loading = true;
      const riskCategoriesOmitTravel = (await getRiskCategories())
        .map((r) => r.id)
        .filter((r) => {
          return r !== TRAVEL_RISK_RATING_ID;
        });

      this.analysisParams.riskCategories = this.hasGrds
        ? [RISK_RATING_SUB.kidnap]
        : riskCategoriesOmitTravel;

      this.loading = false;
    },
    trackTravel() {
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.isos_travel,
        EVENT_ACTIONS.clicked,
        "Watch - " + this.location.code
      );
    },
    handleHeadlinesTabsChange(selected) {
      this.$emit(EMITTED_ACTIONS.HEADLINES_TAB_CHANGE, selected);
    },
  },
  watch: {
    location: {
      handler() {
        if (this.hasGrds) {
          this.tab = this.headlineTabs.updates;
        }
        this.travelParams.aoiId = this.location.code;
        this.analysisParams.aoiId = this.location.code;
      },
      deep: true,
    },
  },
};
</script>

<style>
.layout-toolbar .v-toolbar__content {
  padding-left: 12px !important;
  padding-right: 1px !important;
}
.layout-tabs .v-tab {
  padding-left: 2px !important;
  padding-right: 2px !important;
}
</style>

<style scoped>
.risk-updates-container {
  overflow-y: auto;
  height: 63vh;
}
.tab-text {
  font-size: 10px !important;
}
/* These are for the scroll bar in the content container */
::-webkit-scrollbar {
  width: 8px;
  height: 3px;
}
::-webkit-scrollbar-track {
  background-color: #646464;
  border-radius: 3px;
}
::-webkit-scrollbar-track-piece {
  background-color: #000;
}
::-webkit-scrollbar-thumb {
  height: 50px;
  background-color: #666;
  border-radius: 3px;
}
</style>
